import { Grant } from '../types';

interface SpecializedGrantSource {
  id: string;
  name: string;
  url: string;
  focusAreas: string[];
  eligibility: string[];
  typical_amount: number;
  deadlines: string[];
  description: string;
  requiredMaterials: string[];
}

const SPECIALIZED_SOURCES: SpecializedGrantSource[] = [
  {
    id: 'etp',
    name: 'Employment Training Panel (ETP)',
    url: 'https://etp.ca.gov/program-overview/',
    focusAreas: ['workforce-development', 'training', 'business'],
    eligibility: ['california-business', 'employers'],
    typical_amount: 750000,
    deadlines: ['Rolling'],
    description: 'Provides funding to employers to assist in upgrading the skills of their workers.',
    requiredMaterials: [
      'ETP Application Form',
      'Training Program Outline',
      'Employee Wage Documentation',
      'Business Tax Returns'
    ]
  },
  {
    id: 'ca-grants-portal',
    name: 'California Grants Portal',
    url: 'https://www.grants.ca.gov/',
    focusAreas: ['multiple', 'government', 'nonprofit'],
    eligibility: ['california-organizations', 'nonprofits', 'government-entities'],
    typical_amount: 500000,
    deadlines: ['Various'],
    description: 'Central hub for California state grant opportunities across multiple sectors.',
    requiredMaterials: [
      'Grant Application',
      'Organization Documentation',
      'Project Proposal',
      'Budget Worksheet'
    ]
  },
  {
    id: 'calosba',
    name: 'California Office of the Small Business Advocate',
    url: 'https://calosba.ca.gov/funding-grants-incentives/',
    focusAreas: ['small-business', 'entrepreneurship'],
    eligibility: ['small-business', 'california-business'],
    typical_amount: 250000,
    deadlines: ['Quarterly'],
    description: 'Provides grants and resources for California small businesses.',
    requiredMaterials: [
      'Business Registration',
      'Financial Statements',
      'Tax Returns',
      'Business Plan'
    ]
  },
  {
    id: 'ibank',
    name: 'California IBank Small Business Finance Center',
    url: 'https://ibank.ca.gov/small-business/loan-guarantees/',
    focusAreas: ['small-business', 'economic-development'],
    eligibility: ['california-business', 'small-business'],
    typical_amount: 500000,
    deadlines: ['Rolling'],
    description: 'Offers loan guarantees and direct lending programs for small businesses.',
    requiredMaterials: [
      'Loan Application',
      'Business Financial Statements',
      'Tax Returns',
      'Collateral Documentation'
    ]
  },
  {
    id: 'hcd-grants',
    name: 'California Department of Housing and Community Development',
    url: 'https://www.hcd.ca.gov/grants-and-funding',
    focusAreas: ['housing', 'community-development', 'infrastructure'],
    eligibility: ['local-government', 'nonprofit', 'developers'],
    typical_amount: 1000000,
    deadlines: ['Various'],
    description: 'Provides funding for housing-related projects and community development.',
    requiredMaterials: [
      'Grant Application',
      'Project Proposal',
      'Environmental Review',
      'Site Control Documentation'
    ]
  },
  {
    id: 'ca-film',
    name: 'California Film Commission Tax Credit Program',
    url: 'https://film.ca.gov/tax-credit/',
    focusAreas: ['film', 'television', 'media-production'],
    eligibility: ['production-companies', 'california-based-production'],
    typical_amount: 1500000,
    deadlines: ['Quarterly'],
    description: 'Tax credits for qualified motion picture productions in California.',
    requiredMaterials: [
      'Tax Credit Application',
      'Production Budget',
      'Shooting Schedule',
      'California Spend Report'
    ]
  }
];

export async function fetchSpecializedGrants(filters: {
  focusArea?: string;
  eligibility?: string;
  location?: string;
}): Promise<Grant[]> {
  const filteredSources = SPECIALIZED_SOURCES.filter(source => {
    if (filters.focusArea && !source.focusAreas.includes(filters.focusArea)) {
      return false;
    }
    if (filters.eligibility && !source.eligibility.includes(filters.eligibility)) {
      return false;
    }
    if (filters.location === 'california') {
      return true;
    }
    return true;
  });

  return filteredSources.map(source => ({
    id: source.id,
    title: source.name,
    provider: source.name,
    amount: source.typical_amount,
    deadline: source.deadlines[0],
    matchScore: 0,
    requiredMaterials: source.requiredMaterials,
    applicationUrl: source.url,
    description: source.description,
    category: source.focusAreas.join(', '),
    eligibility: source.eligibility.join(', '),
    fundingType: 'grant',
    postDate: new Date().toISOString(),
    source: 'specialized'
  } as Grant));
}

export async function getSpecializedGrantById(id: string): Promise<Grant | null> {
  const source = SPECIALIZED_SOURCES.find(s => s.id === id);
  if (!source) return null;

  return {
    id: source.id,
    title: source.name,
    provider: source.name,
    amount: source.typical_amount,
    deadline: source.deadlines[0],
    matchScore: 0,
    requiredMaterials: source.requiredMaterials,
    applicationUrl: source.url,
    description: source.description,
    category: source.focusAreas.join(', '),
    eligibility: source.eligibility.join(', '),
    fundingType: 'grant',
    postDate: new Date().toISOString(),
    source: 'specialized'
  };
}